import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import UCSColors from "@ucs/Utils/UCSColors";
import React from "react";
import styled, { css } from "styled-components";

export interface OrderDetailsPaymentInfoContainerStyles {
    container?: InjectableCss;
    shippingInfoSection?: InjectableCss;
    shippingInfoGroup?: InjectableCss;
    shippingInfoHeading?: TypographyPresentationProps;
    shippingInfoLabel?: TypographyPresentationProps;
    shippingInfoText?: TypographyPresentationProps;
}

export const orderDetailsPaymentInfoContainerStyles: OrderDetailsPaymentInfoContainerStyles = {
    container: {
        css: css`
            width: 100%;
            padding: 20px;
            background-color: ${UCSColors.gray10};
            box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.05);
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin-bottom: 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        padding: 20px 10px;
                        gap: 0;
                    `,
                    css`
                        gap: 0;
                    `,
                ]);
            }}
            div[class^="GridWrapper"] {
                h2, h3 {
                    line-height: 16px;
                    letter-spacing: 0.1em;
                    color: ${UCSColors.gray04};
                    text-transform: uppercase;
                    font-size: 13px;
                    font-weight: 700;
                    ${({ theme }) => {
                        return breakpointMediaQueries(
                            theme,
                            [
                                null,
                                css`
                                    font-size: 12px;
                                `,
                            ],
                            "max",
                        );
                    }}
                    &+span {
                        color: #444444;
                        font-size: 18px;
                        font-weight: 400;
                        line-height: 24px;
                    }
                }
            }
        `,
    },
    shippingInfoHeading: {
        variant: "h3",
    },
    shippingInfoSection: {
        css: css`
            width: 100%;
            display: grid;
            grid-template-columns: repeat(3, calc(33.33% - 6px));
            gap: 16px 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        grid-template-columns: 100%;
                        gap: 12px 0;
                    `,
                    css`
                        grid-template-columns: repeat(2, calc(50% - 5px));
                    `,
                    css`
                        grid-template-columns: repeat(2, calc(50% - 5px));
                    `,
                ]);
            }}
        `,
    },
    shippingInfoGroup: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 8px;
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        display: grid;
                        grid-template-columns: repeat(2, calc(50% - 5px));
                        align-items: center;
                        gap: 10px;
                    `,
                ]);
            }}
        `,
    },
    shippingInfoLabel: {
        size: 13,
        weight: 700,
        color: UCSColors.gray04,
        transform: "uppercase",
        css: css`
            line-height: 16px;
            letter-spacing: 0.1em;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 12px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
    shippingInfoText: {
        size: 18,
        weight: 400,
        color: UCSColors.gray03,
        css: css`
            line-height: 24px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-size: 17px;
                        `,
                    ],
                    "max",
                );
            }}
        `,
    },
};

type Props = WidgetProps;

const styles = orderDetailsPaymentInfoContainerStyles;

const Separator = styled.div`
    height: 1px;
    width: 100%;
    margin: 24px 0;
    background-color: ${UCSColors.gray07};
`;

const OrderDetailsPaymentInfoContainer = ({id}: Props) => {
    return (
        <StyledWrapper {...styles.container}>
            <Typography {...styles.shippingInfoHeading}>{translate("Payment Information")}</Typography>
            <Hidden above="sm">
                <Separator />
            </Hidden>
            <Zone contentId={id} zoneName="Content" />
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OrderDetailsPaymentInfoContainer,
    definition: {
        displayName: "Order Payment Info Container",
        allowedContexts: ["OrderDetailsPage"],
        group: "Order Details",
    },
};

export default widgetModule;
